import React, { useContext, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';

import {
  useCaseLock,
  useCaseLockActionGuard,
} from '../../../common/caseLocking/CaseLockContext';
import { FlexRow } from '../../../common/components/v2';
import { DocumentType } from '../../../common/constants/documentType';
import { FeatureFlags } from '../../../common/constants/featureFlags';
import { ReasonsTransform } from '../../../common/constants/reasons';
import { Resource } from '../../../common/constants/resource';
import { TaskState } from '../../../common/constants/states';
import ElementLocators from '../../../common/ElementLocators';
import { FeatureFlagsContext } from '../../../common/featureFlagsWrapper';
import { evidenceExists, getFormattedTimestamp } from '../../../common/helpers';
import { placeEHROrder } from '../../aps/api';
import { ITask } from '../../tasks/interfaces/ITask';
import { EHR } from '../../tasks/types/ehr';
import styles from '../styles.module.scss';
import { Feedback } from './components/Feedback';
import { FileDetail } from './components/FileDetail';
import { FileUpload } from './components/FileUpload';
import { NoraTaskIcon } from './components/NoraTaskIcon';
import { Remarks } from './components/Remarks';
import { TaskDetailsItem } from './components/TaskDetailsItem';

export const EHRDetail = ({
  task,
  uploadHandler,
  downloadHandler,
  taskStateHandler,
  feedbackHandler,
  deleteHandler,
  applicationId,
}: {
  task: ITask;
  uploadHandler: (
    existingTaskData: ITask,
    data: FormData,
    type: string,
  ) => void;
  downloadHandler: (uri: string, fileName: string) => void;
  deleteHandler: (
    existingTaskData: ITask,
    documentId: number,
    reason: string,
  ) => void;
  taskStateHandler: (taskId: number, state: TaskState, reason?: string) => void;
  feedbackHandler: (
    taskId: number,
    sufficent: boolean,
    feedback: string,
  ) => void;
  applicationId?: number;
}) => {
  const { flags } = useContext(FeatureFlagsContext);
  const { isLockedForActions } = useCaseLock();
  const assertCaseActionAllowed = useCaseLockActionGuard();
  const isAutoOrderingEnabled =
    flags[FeatureFlags.UW_NORA_EHR_LABPIQ_AUTO_ORDER_ENABLED] || false;
  const [needsFeedback, setneedsFeedback] = useState(false);
  const [ordering, setOrdering] = useState(false);
  const [orderStatus, setOrderStatus] = useState<'idle' | 'success' | 'error'>(
    'idle',
  );
  const [orderError, setOrderError] = useState('');

  const onFileSelection = (files: any, evidenceType: string) => {
    uploadHandler(task, files, evidenceType);
  };

  const handlePlaceOrder = async () => {
    if (!assertCaseActionAllowed()) {
      return;
    }
    if (!applicationId) {
      setOrderError('Application ID not available');
      return;
    }

    // Prevent multiple rapid clicks
    if (ordering) {
      return;
    }

    setOrdering(true);
    setOrderError('');
    setOrderStatus('idle'); // Reset status

    try {
      const result = await placeEHROrder(applicationId!, task.id);

      // Only set success if no error occurred and result is successful
      if (result && !result.error) {
        setOrderStatus('success');
        setOrderError('');
      } else {
        setOrderStatus('error');
        setOrderError(result?.error || 'Failed to place EHR order');
      }
    } catch (error: any) {
      setOrderStatus('error');
      const errorMessage =
        error.message || error.response?.message || 'Failed to place EHR order';
      setOrderError(errorMessage);
      console.error('Failed to place EHR order', error);
    } finally {
      setOrdering(false);
    }
  };

  const onTaskStateChange = async (state: TaskState, reason?: string) => {
    if (state) {
      if (
        (needsFeedback === false && state === TaskState.COMPLETED) ||
        needsFeedback
      ) {
        setneedsFeedback(true);
        return;
      }
      taskStateHandler(task.id, state, reason);
    }
  };

  const onFeedback = async (
    sufficient: boolean,
    text: string,
    noOperation: boolean,
  ) => {
    if (noOperation) {
      setneedsFeedback(false);
      return;
    }
    setneedsFeedback(false);
    feedbackHandler(task.id, sufficient, text);
  };

  const data: EHR = (task.taskData ?? {}) as EHR;
  const evidence = task.evidence;
  const evidenceReasons: string[] = (data?.reason || []).map((item: string) => {
    return ReasonsTransform.getReasonDisplay(item);
  });

  return (
    <Box data-label={ElementLocators.LOCATOR_MIB_CODE_DETAILS_SECTION}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
        mb={0.75}
        id={task.id.toString()}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={0.5}>
            <NoraTaskIcon task={task} onTaskStateClick={onTaskStateChange} />
          </Box>
          <Typography variant="h2">{Resource.TASK_TITLE_EHR}</Typography>
        </Box>
        <Box>
          <Typography color="textSecondary" variant="subtitle2">
            {getFormattedTimestamp(task.retriedAt ?? task.createdAt)}
          </Typography>
        </Box>
      </Box>
      {task.state === TaskState.WAIVED && task.waiveReason && (
        <FlexRow className={styles.TaskSubtitle}>
          <strong>{Resource.WAIVE_REASON}:</strong>&nbsp;{task.waiveReason}
        </FlexRow>
      )}
      {needsFeedback ? (
        <Feedback show={needsFeedback} feedbackHandler={onFeedback}></Feedback>
      ) : task.sufficient != null ? (
        <Remarks
          sufficient={task.sufficient}
          feedback={task.feedback}
        ></Remarks>
      ) : (
        <></>
      )}

      {evidenceExists(evidence) && evidence && (
        <FileDetail
          documents={evidence.documents}
          fileOpenHandler={downloadHandler}
          fileDeletehandler={(documentId: number, reason: string) =>
            deleteHandler(task, documentId, reason)
          }
        />
      )}

      {/* Show upload button:
          - Show upload when task is ORDERED (or other non-REQUESTED/non-COMPLETED states) and no evidence exists
          - Do NOT show upload button when task is in REQUESTED state
      */}
      {task.state !== TaskState.COMPLETED &&
        task.state !== TaskState.REQUESTED &&
        !evidenceExists(evidence) && (
          <FileUpload
            leftItem={Resource.FILES}
            selectedFiles={onFileSelection}
            type={DocumentType.EHR}
            ongoingUpload={task.ongoingUpload?.[DocumentType.EHR]}
          />
        )}

      {/* Manual ordering button - show when feature flag is ON and task is ERROR (auto-order failed) */}
      {isAutoOrderingEnabled &&
        task.state === TaskState.ERROR &&
        !isLockedForActions && (
          <Box mt={3}>
            <Alert severity="info" sx={{ mb: 2 }}>
              This EHR order needs to be placed manually. Click the button below
              to order from ExamOne.
            </Alert>
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={handlePlaceOrder}
              disabled={ordering || orderStatus === 'success'}
              startIcon={
                ordering && <CircularProgress size={20} color="inherit" />
              }
            >
              {orderStatus === 'success' && !orderError
                ? 'Order Placed ✓'
                : 'Order EHR from ExamOne'}
            </Button>

            {orderStatus === 'success' && !orderError && (
              <Alert severity="success" sx={{ mt: 2 }}>
                EHR order successfully placed with ExamOne
              </Alert>
            )}

            {(orderStatus === 'error' || orderError) && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {orderError || 'Failed to place EHR order'}
              </Alert>
            )}
          </Box>
        )}

      {(evidenceReasons?.length > 0 || data?.reasonDescription) && (
        <>
          <FlexRow className={styles.TaskSubtitle}>{Resource.REASON}</FlexRow>
          {evidenceReasons?.length > 0 && (
            <TaskDetailsItem
              leftItem={Resource.EVIDENCE}
              rightItem={evidenceReasons.join(Resource.NORA_SEPARATOR)}
            />
          )}
          {data?.reasonDescription && (
            <TaskDetailsItem
              leftItem={Resource.DESCRIPTION}
              rightItem={data.reasonDescription}
            />
          )}
        </>
      )}
    </Box>
  );
};
